<template>
    <div :class="ui.root({ class: sizeClass })">
        <NuxtImg v-if="avatarSrc" :src="avatarSrc" :alt="alt" :class="ui.image()" />

        <span v-else :class="ui.fallbackText({ class: textClass })">
            {{ getUserInitials(fullName) }}
        </span>
    </div>
</template>

<script setup lang="ts">
    import userAvatarTheme from '@/assets/css/themes/custom-components/userAvatar';

    const avatarPalettes = getObjectKeys(userAvatarTheme.variants.palette);

    const props = defineProps({
        userId: {
            type: Number
        },
        fullName: {
            type: String
        },
        alt: {
            type: String,
            default: ''
        },
        sizeClass: {
            type: String
        },
        textClass: {
            type: String
        }
    });

    const ui = useComponentUi('userAvatar', () => ({
        palette: avatarPalettes[hashString(props.fullName || 'U') % avatarPalettes.length]
    }));

    const instanceId = useId();

    function hashString(str: string) {
        let hash = 0;

        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }

        return Math.abs(hash);
    }

    function getUserInitials(fullName?: string) {
        if (fullName === undefined) {
            return undefined;
        }

        const name = fullName.trim();
        const parts = name.split(/\s+/).filter(Boolean);
        const firstPart = parts[0]!;
        const secondPart = parts[1];

        if (secondPart !== undefined) {
            return (firstPart.charAt(0) + secondPart.charAt(0)).toUpperCase();
        }

        return firstPart.slice(0, 2).toUpperCase();
    }

    const { src: avatarSrc, execute } = useBlobPreview(
        () => loadUserAvatarBlob(props.userId),
        () => `user-avatar-key-${instanceId}-${props.userId ?? 'empty'}`
    );

    defineExpose({
        refresh: execute
    });
</script>
